import React, { useState } from 'react';
import { UserTalent, Talent } from '../../../services/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserTalentsSectionProps {
  userTalents: UserTalent[];
  allTalents: Talent[];
  onAddTalent: (talent: Talent) => void;
  onUpdateTalentDetail: (talentId: number, field: 'erfaringsnivaa' | 'notater', value: any) => void;
  onRemoveTalent: (talentId: number) => void;
  loading: boolean;
  /**
   * Låser fjern-knappen for talent brukeren er bemannet med i en produksjon
   * (`i_produksjon: true`). Brukes i «Mine talenter» for ikke-admin; admin-dialogen
   * (UserDialog) sender ikke denne propen og er uendret.
   */
  lockTalentsInProduction?: boolean;
}

const TALENT_I_PRODUKSJON_TEKST =
  'Kan ikke slette et talent når du er oppført i en produksjon';

const UserTalentsSection: React.FC<UserTalentsSectionProps> = ({
  userTalents,
  allTalents,
  onAddTalent,
  onUpdateTalentDetail,
  onRemoveTalent,
  loading,
  lockTalentsInProduction = false,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  // Tilgjengelige talents (ikke allerede lagt til)
  const availableTalents = allTalents.filter(
    (t) => !userTalents.some((ut) => ut.talent_id === t.id)
  );

  return (
    <div>
      <h4 className="text-sm font-semibold mb-2">Talents ({userTalents.length})</h4>

      {/* Quick-add combobox */}
      <Popover open={searchOpen} onOpenChange={setSearchOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={searchOpen}
            disabled={loading}
            className="w-full justify-between font-normal text-muted-foreground"
          >
            Søk og legg til talent...
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Søk etter talent..." />
            <CommandList>
              <CommandEmpty>Ingen flere tilgjengelige talents</CommandEmpty>
              <CommandGroup>
                {availableTalents.map((talent) => (
                  <CommandItem
                    key={talent.id}
                    value={`${talent.navn} ${talent.kategori_navn}`}
                    onSelect={() => {
                      onAddTalent(talent);
                      setSearchOpen(false);
                    }}
                  >
                    {talent.navn} • {talent.kategori_navn}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Talent badges/chips */}
      {userTalents.length > 0 && (
        <div className="mt-4 space-y-2">
          {userTalents.map((talent) => {
            const isLocked = lockTalentsInProduction && talent.i_produksjon === true;
            const lockedTextId = `talent-i-produksjon-${talent.talent_id}`;

            return (
            <div
              key={talent.talent_id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-card border rounded-lg hover:shadow-sm transition-shadow"
            >
              {/* Talent info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-foreground truncate">
                    {talent.talent_navn}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {talent.kategori_navn}
                  </span>
                </div>
              </div>

              {/* Erfaringsnivå select (kompakt) */}
              <div className="flex items-center gap-2">
                <Select
                  value={talent.erfaringsnivaa}
                  onValueChange={(value) =>
                    onUpdateTalentDetail(talent.talent_id, 'erfaringsnivaa', value)
                  }
                  disabled={loading}
                >
                  <SelectTrigger className="h-8 w-[140px] sm:w-[160px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grunnleggende">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-slate-400" />
                        <span>Grunnleggende</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="middels">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-400" />
                        <span>Middels</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="avansert">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-400" />
                        <span>Avansert</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="ekspert">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-400" />
                        <span>Ekspert</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Fjern-knapp */}
                {(() => {
                  const removeButton = (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn('h-8 w-8 hover:bg-destructive/10 text-destructive shrink-0')}
                      onClick={() => onRemoveTalent(talent.talent_id)}
                      disabled={loading || isLocked}
                      aria-label={`Fjern ${talent.talent_navn}`}
                      aria-describedby={isLocked ? lockedTextId : undefined}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  );

                  if (!isLocked) {
                    return removeButton;
                  }

                  return (
                    <span
                      title={TALENT_I_PRODUKSJON_TEKST}
                      tabIndex={0}
                      className="inline-flex"
                    >
                      {removeButton}
                    </span>
                  );
                })()}
                {isLocked && (
                  <span id={lockedTextId} className="sr-only">
                    {TALENT_I_PRODUKSJON_TEKST}
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Tom state */}
      {userTalents.length === 0 && (
        <div className="mt-4 p-6 text-center bg-muted/30 border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">
            Ingen talents lagt til ennå. Bruk søkefeltet over for å legge til.
          </p>
        </div>
      )}
    </div>
  );
};

export default UserTalentsSection;
